import React, { useEffect, useMemo, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { isDocumentFileMissing, saveBlob, useDocumentFile } from '@/services/documentService';
import { previewKind, previewMimeType } from '@/utils/documentPreview';

interface DocumentViewerProps {
  documentId: string;
  fileName: string;
  /** The PDF page to open at */
  page?: number;
  /**
   * Raise it to go to `page` again. A loaded PDF ignores a changed `#page=` (Chrome), so every jump is
   * a new frame, which also serves a second click on the same page. The file is not fetched again.
   */
  jump?: number;
  className?: string;
}

/**
 * A PDF or image shown in the app (#29, Decision 39). The bytes come through apiClient, never from a
 * URL the browser fetches itself, and render under the type the file extension gives. The PDF frame
 * has no `sandbox`: Chrome's viewer refuses to load in one, so that typing is what keeps a stored file
 * from running as a page of this origin.
 */
export const DocumentViewer: React.FC<DocumentViewerProps> = ({ documentId, fileName, page, jump = 0, className }) => {
  const kind = previewKind(fileName);
  const mime = previewMimeType(fileName);
  const { data, error, isFetching, refetch } = useDocumentFile(documentId, kind !== null);

  const typed = useMemo(() => (data && mime ? data.slice(0, data.size, mime) : null), [data, mime]);

  // The URL belongs to this component, not the query cache: garbage collection would not release it
  const [objectUrl, setObjectUrl] = useState<{ source: Blob; url: string } | null>(null);
  useEffect(() => {
    if (!typed) return;
    const url = URL.createObjectURL(typed);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- an object URL is an external resource: made here so this effect can release it
    setObjectUrl({ source: typed, url });
    return () => URL.revokeObjectURL(url);
  }, [typed]);
  const url = objectUrl?.source === typed ? objectUrl?.url : undefined;

  const canShowPdf = navigator.pdfViewerEnabled !== false;

  let body: React.ReactNode;
  if (!kind) {
    body = <p className="p-4 text-sm text-grey-600">This file can only be downloaded.</p>;
  } else if (error) {
    const missing = isDocumentFileMissing(error);
    body = (
      <Alert variant="destructive" className="m-4 w-auto" data-testid="document-viewer-error">
        <AlertDescription className="flex flex-wrap items-center gap-3">
          {missing ? 'The file is missing from storage.' : 'The file could not be loaded.'}
          {!missing && (
            <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className="h-4 w-4 mr-1.5" aria-hidden="true" />
              Try again
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  } else if (!url) {
    body = <Skeleton className="h-full w-full" data-testid="document-viewer-loading" />;
  } else if (kind === 'image') {
    body = (
      <div className="flex h-full items-center justify-center overflow-auto bg-grey-100">
        <img src={url} alt={fileName} className="max-h-full max-w-full object-contain" data-testid="document-viewer-image" />
      </div>
    );
  } else if (!canShowPdf) {
    body = (
      <p className="p-4 text-sm text-grey-600" data-testid="document-viewer-fallback">
        This browser cannot show PDFs here. Download the file to read it.
      </p>
    );
  } else {
    body = (
      <iframe
        key={jump}
        src={page ? `${url}#page=${page}` : url}
        title={fileName}
        className="h-full w-full border-0"
        data-testid="document-viewer-pdf"
      />
    );
  }

  return (
    <div className={cn('flex min-h-0 flex-col', className)} data-testid="document-viewer">
      {typed && (
        <div className="flex items-center justify-between gap-3 pb-2">
          <p className="text-xs text-grey-600">{kind === 'pdf' && page ? `Requested page ${page}` : ''}</p>
          <Button variant="outline" size="sm" onClick={() => saveBlob(typed, fileName)} data-testid="document-viewer-download">
            <Download className="h-4 w-4 mr-1.5" aria-hidden="true" />
            Download
          </Button>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-hidden rounded-md border border-border">{body}</div>
    </div>
  );
};
